import React from 'react';
import {
  CHECKLIST_STATUS_OPTIONS,
  FUEL_TYPE_OPTIONS,
  TIRE_TYPE_OPTIONS,
  TREAD_STATUS_OPTIONS,
  YES_NO_OPTIONS,
  getVehicleChecklistConfig,
  getVehicleChecklistTextFields,
} from './vehicleChecklistConfig';

const FIELD_GROUPS = [
  {
    title: 'Contexto de la operación',
    fields: ['project', 'area'],
  },
  {
    title: 'Información general del vehículo',
    fields: ['owner_identification', 'color', 'engine_number', 'chassis_number', 'owner_name', 'passenger_capacity'],
  },
  {
    title: 'Datos generales del conductor',
    fields: ['driver_role', 'driver_license_number', 'driver_license_categories', 'driver_license_expiry'],
  },
  {
    title: 'Inventario',
    fields: ['initial_mileage', 'final_mileage', 'next_oil_change_km'],
  },
];

const textFieldMap = Object.fromEntries(getVehicleChecklistTextFields().map((field) => [field.key, field]));

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function PhotoInput({ label, photos, onAdd, onRemove }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium mb-1">{label}</label>
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
          multiple
          className="block w-full text-sm text-gray-700"
          onChange={onAdd}
        />
      </div>
      {Array.isArray(photos) && photos.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
          {photos.map((photo, index) => (
            <div key={`${photo.name || 'foto'}-${index}`} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <img
                src={photo.dataUrl}
                alt={photo.name || `Foto ${index + 1}`}
                className="h-32 w-full object-cover"
              />
              <div className="flex items-center justify-between gap-2 p-2">
                <div className="truncate text-xs text-gray-600">{photo.name || `Foto ${index + 1}`}</div>
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                >
                  Quitar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SelectField({ label, value, options, onChange, required = false }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <select
        className="w-full border rounded p-2"
        value={value || ''}
        required={required}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Seleccionar</option>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </div>
  );
}

export default function VehicleChecklistForm({
  vehicleType,
  value,
  onChange,
  modeLabel,
}) {
  const config = getVehicleChecklistConfig(vehicleType);

  if (!config) {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
        Configura primero el `Tipo de vehículo` en Flota para habilitar el checklist de {modeLabel.toLowerCase()}.
      </div>
    );
  }

  const setTextField = (key, fieldValue) => {
    onChange({
      ...value,
      text: {
        ...(value?.text || {}),
        [key]: fieldValue,
      },
    });
  };

  const setStatusField = (key, fieldValue) => {
    onChange({
      ...value,
      statuses: {
        ...(value?.statuses || {}),
        [key]: fieldValue,
      },
    });
  };

  const setTireField = (key, fieldValue) => {
    onChange({
      ...value,
      tire_data: {
        ...(value?.tire_data || {}),
        [key]: fieldValue,
      },
    });
  };

  const setChecklistField = (key, fieldValue) => {
    onChange({
      ...value,
      [key]: fieldValue,
    });
  };

  const setChecklistPhotos = (key, photos) => {
    setChecklistField(key, photos);
  };

  const handlePhotoAdd = async (key, event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      const photoEntries = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          dataUrl: await readFileAsDataUrl(file),
        }))
      );
      setChecklistPhotos(key, [...(Array.isArray(value?.[key]) ? value[key] : []), ...photoEntries]);
    } catch (_) {
      alert('No fue posible cargar una o más fotos.');
    } finally {
      event.target.value = '';
    }
  };

  const handlePhotoRemove = (key, indexToRemove) => {
    const currentPhotos = Array.isArray(value?.[key]) ? value[key] : [];
    setChecklistPhotos(
      key,
      currentPhotos.filter((_, index) => index !== indexToRemove)
    );
  };

  const findingsValue = value?.findings_notes || (Array.isArray(value?.hallazgos) ? value.hallazgos.filter(Boolean).join('\n') : '');

  return (
    <div className="space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-orange-700">{config.code}</div>
        <h4 className="mt-1 text-base font-semibold text-gray-900">{config.title}</h4>
        <p className="mt-1 text-sm text-gray-600">
          Formato aplicado automáticamente para vehículo tipo <span className="font-semibold">{vehicleType}</span> en {modeLabel.toLowerCase()}.
        </p>
      </div>

      {FIELD_GROUPS.map((group, index) => (
        <div
          key={group.title}
          id={`checklist-group-${index}`}
          className="scroll-mt-28 rounded-lg border border-gray-200 bg-white p-4"
        >
          <h5 className="mb-3 text-sm font-semibold text-gray-900">{group.title}</h5>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {group.fields.map((fieldKey) => (
              <div key={fieldKey}>
                <label className="block text-sm font-medium mb-1">{textFieldMap[fieldKey]?.label || fieldKey}</label>
                <input
                  type="text"
                  className="w-full border rounded p-2"
                  value={value?.text?.[fieldKey] || ''}
                  onChange={(e) => setTextField(fieldKey, e.target.value)}
                />
              </div>
            ))}
            {group.title === 'Inventario' && (
              <>
                <SelectField
                  label="Tipo de combustible"
                  value={value?.fuel_type || ''}
                  options={FUEL_TYPE_OPTIONS}
                  onChange={(fieldValue) => onChange({ ...value, fuel_type: fieldValue })}
                />
                <SelectField
                  label="Hoja de vida del vehículo"
                  value={value?.has_vehicle_life_sheet || ''}
                  options={YES_NO_OPTIONS}
                  onChange={(fieldValue) => onChange({ ...value, has_vehicle_life_sheet: fieldValue })}
                />
                <SelectField
                  label="Carpeta de documentos del vehículo"
                  value={value?.has_document_folder || ''}
                  options={YES_NO_OPTIONS}
                  onChange={(fieldValue) => onChange({ ...value, has_document_folder: fieldValue })}
                />
              </>
            )}
          </div>
        </div>
      ))}

      {config.sections.map((section, index) => (
        <div
          key={section.title}
          id={`checklist-section-${index}`}
          className="scroll-mt-28 rounded-lg border border-gray-200 bg-white p-4"
        >
          <h5 className="mb-3 text-sm font-semibold text-gray-900">{section.title}</h5>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {section.items.map((item) => (
              <SelectField
                key={item.key}
                label={item.label}
                value={value?.statuses?.[item.key] || ''}
                options={CHECKLIST_STATUS_OPTIONS}
                onChange={(fieldValue) => setStatusField(item.key, fieldValue)}
              />
            ))}
          </div>
        </div>
      ))}

      <div id="checklist-physical" className="scroll-mt-28 rounded-lg border border-gray-200 bg-white p-4">
        <h5 className="mb-3 text-sm font-semibold text-gray-900">Estado físico del vehículo</h5>
        <div className="space-y-4">
          <textarea
            className="w-full rounded border p-3"
            rows="4"
            placeholder="Relaciona rayones, golpes, desperfectos o novedad física del vehículo"
            value={value?.physical_condition_notes || ''}
            onChange={(e) => setChecklistField('physical_condition_notes', e.target.value)}
          />
          <PhotoInput
            label="Fotos del estado físico"
            photos={value?.physical_condition_photos || []}
            onAdd={(event) => handlePhotoAdd('physical_condition_photos', event)}
            onRemove={(index) => handlePhotoRemove('physical_condition_photos', index)}
          />
        </div>
      </div>

      <div id="checklist-tires" className="scroll-mt-28 rounded-lg border border-gray-200 bg-white p-4">
        <h5 className="mb-3 text-sm font-semibold text-gray-900">Llantas</h5>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div>
            <label className="block text-sm font-medium mb-1">Referencia llantas delanteras</label>
            <input
              type="text"
              className="w-full border rounded p-2"
              value={value?.tire_data?.front_reference || ''}
              onChange={(e) => setTireField('front_reference', e.target.value)}
            />
          </div>
          <SelectField
            label="Tipo llantas delanteras"
            value={value?.tire_data?.front_type || ''}
            options={TIRE_TYPE_OPTIONS}
            onChange={(fieldValue) => setTireField('front_type', fieldValue)}
          />
          <div>
            <label className="block text-sm font-medium mb-1">Referencia llantas traseras</label>
            <input
              type="text"
              className="w-full border rounded p-2"
              value={value?.tire_data?.rear_reference || ''}
              onChange={(e) => setTireField('rear_reference', e.target.value)}
            />
          </div>
          <SelectField
            label="Tipo llantas traseras"
            value={value?.tire_data?.rear_type || ''}
            options={TIRE_TYPE_OPTIONS}
            onChange={(fieldValue) => setTireField('rear_type', fieldValue)}
          />
          <div>
            <label className="block text-sm font-medium mb-1">Referencia llanta de repuesto</label>
            <input
              type="text"
              className="w-full border rounded p-2"
              value={value?.tire_data?.spare_reference || ''}
              onChange={(e) => setTireField('spare_reference', e.target.value)}
            />
          </div>
          <SelectField
            label="Tipo llanta de repuesto"
            value={value?.tire_data?.spare_type || ''}
            options={TIRE_TYPE_OPTIONS}
            onChange={(fieldValue) => setTireField('spare_type', fieldValue)}
          />
          <SelectField
            label="Labrado delantera izquierda"
            value={value?.tire_data?.tread_front_left || ''}
            options={TREAD_STATUS_OPTIONS}
            onChange={(fieldValue) => setTireField('tread_front_left', fieldValue)}
          />
          <SelectField
            label="Labrado delantera derecha"
            value={value?.tire_data?.tread_front_right || ''}
            options={TREAD_STATUS_OPTIONS}
            onChange={(fieldValue) => setTireField('tread_front_right', fieldValue)}
          />
          <SelectField
            label="Labrado trasera izquierda"
            value={value?.tire_data?.tread_rear_left || ''}
            options={TREAD_STATUS_OPTIONS}
            onChange={(fieldValue) => setTireField('tread_rear_left', fieldValue)}
          />
          <SelectField
            label="Labrado trasera derecha"
            value={value?.tire_data?.tread_rear_right || ''}
            options={TREAD_STATUS_OPTIONS}
            onChange={(fieldValue) => setTireField('tread_rear_right', fieldValue)}
          />
        </div>
      </div>

      <div id="checklist-findings" className="scroll-mt-28 rounded-lg border border-gray-200 bg-white p-4">
        <h5 className="mb-3 text-sm font-semibold text-gray-900">Hallazgos</h5>
        <div className="space-y-4">
          <textarea
            className="w-full rounded border p-3"
            rows="5"
            placeholder="Describe los hallazgos identificados durante la asignación o devolución"
            value={findingsValue}
            onChange={(e) => setChecklistField('findings_notes', e.target.value)}
          />
          <PhotoInput
            label="Fotos de hallazgos"
            photos={value?.findings_photos || []}
            onAdd={(event) => handlePhotoAdd('findings_photos', event)}
            onRemove={(index) => handlePhotoRemove('findings_photos', index)}
          />
        </div>
      </div>
    </div>
  );
}
